
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Message } from "@/types/message";
import { AssistantData } from "@/types/assistant";
import { useToast } from "@/components/ui/use-toast";
import { processChatMessage } from "@/utils/chatResponseHandler";

export const useChat = (
  assistantData: AssistantData | null
) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: uuidv4(),
      content,
      isBot: false,
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setIsProcessing(true);
    
    try {
      const botMessage = await processChatMessage(
        content, 
        messages, 
        assistantData
      );
      
      // Show error toast if there was an error
      if (botMessage.error) {
        toast({
          title: "Edge Function Error",
          description: `Status code: ${botMessage.statusCode}. Please check your OpenAI API key or connection.`,
          variant: "destructive",
        });
      }
      
      // Add the bot message to the chat
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("⭐ useChat: Unexpected error in handleSendMessage:", error);
      console.error("⭐ useChat: Error details:", error instanceof Error ? 
        { name: error.name, message: error.message, stack: error.stack } : 
        String(error));
      
      // Show generic error toast
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred while processing your message.",
        variant: "destructive",
      });
      
      // Add a generic error message
      setMessages((prev) => [...prev, {
        id: uuidv4(),
        content: "Sorry, an unexpected error occurred. Please try again.",
        isBot: true,
        timestamp: new Date(),
        error: true
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    messages,
    isProcessing,
    handleSendMessage
  };
};
